/** Structured conformal coat callout. Revision letters follow Hub document control: the first version is A. */

export const CONFORMAL_COAT_TEMPLATE = "conformal_coat_callout";

export const COAT_COLORS = ["yellow", "blue", "brown", "red", "purple", "orange", "grey"] as const;

export type CoatColor = (typeof COAT_COLORS)[number];

export type CoatCalloutSide = {
  name: string;
  recognize: string;
  installed: string;
};

export type CoatCalloutIdentifyRow = {
  assembly: string;
  module: string;
  sim: string;
};

export type CoatCalloutRow = {
  option: string;
  color: CoatColor;
  sites: string;
  wireless: string;
  nonWireless: string;
  /** When true, this row shares the action cells of the previous row. */
  continueFromPrevious: boolean;
};

export type CoatCalloutBody = {
  title: string;
  drawing: string;
  company: string;
  date: string;
  wirelessAssembly: string;
  nonWirelessAssembly: string;
  intro: string;
  sideNote: string;
  sides: CoatCalloutSide[];
  identifyNote: string;
  legend: string;
  identifyRows: CoatCalloutIdentifyRow[];
  hybridIntro: string;
  hybridBullets: string[];
  cureNote: string;
  coatIntro: string;
  coatRows: CoatCalloutRow[];
  closing: string;
};

export type CoatFieldChange = {
  fieldName: string;
  changedFrom: string;
  changedTo: string;
};

const TEXT_LIMIT = 4000;

export function conformalCoatCalloutSeed(): CoatCalloutBody {
  return {
    title: "Conformal coat callout, 15-1485-R17",
    drawing: "CONFORMAL-1485S-R17",
    company: "Smart Grid Solutions",
    date: "24 Sep 2026",
    wirelessAssembly: "15-1485-W-R17",
    nonWirelessAssembly: "15-1485-N-R17",
    intro:
      "This instruction selects the options on drawing CONFORMAL-1485S-R17. Production part numbers are 15-1485-W-R17 (wireless) and 15-1485-N-R17 (non-wireless). The drawing lists choices. This page is the choice. Where they differ, follow this page.",
    sideNote:
      "Use these names in questions and in the lot record. J2, J4, J8, and FTDI2 are the same 3-pin header shape. They are not the same site. Use the reference designator.",
    sides: [
      {
        name: "Top. Also called the back.",
        recognize: "The side with the hybrid capacitor, C35 or C57.",
        installed: "Hidden when the board is installed.",
      },
      {
        name: "Bottom. Also called the front.",
        recognize: "The side with the LEDs. No hybrid capacitor.",
        installed: "Exposed when the board is installed.",
      },
    ],
    identifyNote:
      "15-1485-W-R17 and 15-1485-N-R17 use the same PCB. The bottom of the board is marked PCB-1485S-17. That mark does not tell the two assemblies apart.",
    legend: "PCB-1485S-17",
    identifyRows: [
      { assembly: "15-1485-W-R17, wireless", module: "Installed", sim: "Installed" },
      { assembly: "15-1485-N-R17, non-wireless", module: "Not installed", sim: "Not installed" },
    ],
    hybridIntro:
      "C35 and C57 are used on both 15-1485-W-R17 and 15-1485-N-R17. They are equivalent for this product. They use different footprints. A board gets one of them, not both.",
    hybridBullets: [
      "If C35 or C57 is already installed, coat that part and its pads. Do not mask it. Coat the empty footprint as well.",
      "If neither part is installed, mask both C35 and C57 until the part is chosen. After that part is installed, touch up both footprints so the empty one is coated too.",
    ],
    cureNote:
      "Do not set the cure oven above 85\u00b0C. Cure only long enough for the boards to be handled and shipped. The coat keeps curing through the rest of the manufacturing process.",
    coatIntro:
      "Confirm which board you have with section 2. Read only that assembly's column. Comms (blue) is the only row that differs.",
    coatRows: [
      {
        option: "Base, yellow",
        color: "yellow",
        sites: "RF+, RF-, CPU+, CPU-, AC1, AC2",
        wireless: "Mask at the initial coat. Coat those holes in the touch-up step. Always.",
        nonWireless: "Mask at the initial coat. Coat those holes in the touch-up step. Always.",
        continueFromPrevious: false,
      },
      {
        option: "Comms, blue",
        color: "blue",
        sites: "Top: J1 and J3.\nBottom: J6.",
        wireless: "Mask J1, J3, and J6.",
        nonWireless: "Coat J1, J3, and J6.",
        continueFromPrevious: false,
      },
      {
        option: "UART / terminal, brown",
        color: "brown",
        sites: "FTDI2",
        wireless: "Top: coat.\nBottom: mask. Do not coat the bottom pads or holes.",
        nonWireless: "Top: coat.\nBottom: mask. Do not coat the bottom pads or holes.",
        continueFromPrevious: false,
      },
      {
        option: "Programming, red",
        color: "red",
        sites: "J10",
        wireless:
          "Top: coat.\nBottom: mask if the added mask cost is $0.25 or less per board. If masking costs more than $0.25 per board, coat the bottom and do not mask it.",
        nonWireless:
          "Top: coat.\nBottom: mask if the added mask cost is $0.25 or less per board. If masking costs more than $0.25 per board, coat the bottom and do not mask it.",
        continueFromPrevious: false,
      },
      {
        option: "Comms SW, purple",
        color: "purple",
        sites: "J2, J4, and J8",
        wireless: "Coat both sides. Do not mask.",
        nonWireless: "Coat both sides. Do not mask.",
        continueFromPrevious: false,
      },
      {
        option: "Hybrid capacitor, orange",
        color: "orange",
        sites: "C35, Xeno XLC-1325",
        wireless: "See section 3. Coat both sides. Do not mask.",
        nonWireless: "See section 3. Coat both sides. Do not mask.",
        continueFromPrevious: false,
      },
      {
        option: "Hybrid capacitor, grey",
        color: "grey",
        sites: "C57, Tadiran HLC-1520A/T",
        wireless: "",
        nonWireless: "",
        continueFromPrevious: true,
      },
    ],
    closing:
      "Email selections of these options are closed by this instruction. Do not keep out Comms SW. Do not treat Comms and Comms SW as the same region.",
  };
}

function requireText(value: unknown, label: string): string {
  if (typeof value !== "string") throw new Error(`${label} must be text.`);
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is required.`);
  if (trimmed.length > TEXT_LIMIT) throw new Error(`${label} is too long.`);
  return value.replace(/\r\n/g, "\n");
}

function optionalText(value: unknown, label: string): string {
  if (typeof value !== "string") throw new Error(`${label} must be text.`);
  if (value.length > TEXT_LIMIT) throw new Error(`${label} is too long.`);
  return value.replace(/\r\n/g, "\n");
}

export function parseCoatCalloutBody(value: unknown): CoatCalloutBody {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Work instruction body must be an object.");
  }
  const record = value as Record<string, unknown>;
  const sides = Array.isArray(record.sides) ? record.sides : null;
  const identifyRows = Array.isArray(record.identifyRows) ? record.identifyRows : null;
  const hybridBullets = Array.isArray(record.hybridBullets) ? record.hybridBullets : null;
  const coatRows = Array.isArray(record.coatRows) ? record.coatRows : null;
  if (!sides || sides.length < 1 || sides.length > 8) throw new Error("Side rows are required.");
  if (!identifyRows || identifyRows.length < 1 || identifyRows.length > 8) throw new Error("Identify rows are required.");
  if (!hybridBullets || hybridBullets.length < 1 || hybridBullets.length > 12) throw new Error("Hybrid steps are required.");
  if (!coatRows || coatRows.length < 1 || coatRows.length > 24) throw new Error("Coat rows are required.");

  return {
    title: requireText(record.title, "Title"),
    drawing: requireText(record.drawing, "Drawing"),
    company: requireText(record.company, "Company"),
    date: requireText(record.date, "Date"),
    wirelessAssembly: requireText(record.wirelessAssembly, "Wireless assembly"),
    nonWirelessAssembly: requireText(record.nonWirelessAssembly, "Non-wireless assembly"),
    intro: requireText(record.intro, "Introduction"),
    sideNote: requireText(record.sideNote, "Side note"),
    sides: sides.map((side, index) => {
      if (side == null || typeof side !== "object") throw new Error(`Side ${index + 1} is invalid.`);
      const row = side as Record<string, unknown>;
      return {
        name: requireText(row.name, `Side ${index + 1} name`),
        recognize: requireText(row.recognize, `Side ${index + 1} recognition`),
        installed: requireText(row.installed, `Side ${index + 1} installed`),
      };
    }),
    identifyNote: requireText(record.identifyNote, "Identify note"),
    legend: requireText(record.legend, "Board mark"),
    identifyRows: identifyRows.map((entry, index) => {
      if (entry == null || typeof entry !== "object") throw new Error(`Identify row ${index + 1} is invalid.`);
      const row = entry as Record<string, unknown>;
      return {
        assembly: requireText(row.assembly, `Identify row ${index + 1} assembly`),
        module: requireText(row.module, `Identify row ${index + 1} module`),
        sim: requireText(row.sim, `Identify row ${index + 1} SIM`),
      };
    }),
    hybridIntro: requireText(record.hybridIntro, "Hybrid introduction"),
    hybridBullets: hybridBullets.map((bullet, index) => requireText(bullet, `Hybrid step ${index + 1}`)),
    cureNote: requireText(record.cureNote, "Cure note"),
    coatIntro: requireText(record.coatIntro, "Coat introduction"),
    coatRows: coatRows.map((entry, index) => {
      if (entry == null || typeof entry !== "object") throw new Error(`Coat row ${index + 1} is invalid.`);
      const row = entry as Record<string, unknown>;
      const color = row.color;
      if (typeof color !== "string" || !COAT_COLORS.includes(color as CoatColor)) {
        throw new Error(`Coat row ${index + 1} color is invalid.`);
      }
      return {
        option: requireText(row.option, `Coat row ${index + 1} option`),
        color: color as CoatColor,
        sites: optionalText(row.sites, `Coat row ${index + 1} sites`),
        wireless: optionalText(row.wireless, `Coat row ${index + 1} wireless`),
        nonWireless: optionalText(row.nonWireless, `Coat row ${index + 1} non-wireless`),
        continueFromPrevious: row.continueFromPrevious === true,
      };
    }),
    closing: requireText(record.closing, "Closing"),
  };
}

function fieldMap(body: CoatCalloutBody): Map<string, string> {
  const map = new Map<string, string>();
  const put = (name: string, value: string) => map.set(name, value);
  put("Title", body.title);
  put("Drawing", body.drawing);
  put("Company", body.company);
  put("Date", body.date);
  put("Wireless assembly", body.wirelessAssembly);
  put("Non-wireless assembly", body.nonWirelessAssembly);
  put("Introduction", body.intro);
  put("Side note", body.sideNote);
  body.sides.forEach((side, index) => {
    put(`Side ${index + 1} name`, side.name);
    put(`Side ${index + 1} recognition`, side.recognize);
    put(`Side ${index + 1} in the device`, side.installed);
  });
  put("Identify note", body.identifyNote);
  put("Board mark", body.legend);
  body.identifyRows.forEach((row, index) => {
    put(`Identify row ${index + 1} assembly`, row.assembly);
    put(`Identify row ${index + 1} module`, row.module);
    put(`Identify row ${index + 1} SIM`, row.sim);
  });
  put("Hybrid introduction", body.hybridIntro);
  body.hybridBullets.forEach((bullet, index) => put(`Hybrid step ${index + 1}`, bullet));
  put("Cure note", body.cureNote);
  put("Coat introduction", body.coatIntro);
  body.coatRows.forEach((row, index) => {
    put(`Coat row ${index + 1} option`, row.option);
    put(`Coat row ${index + 1} color`, row.color);
    put(`Coat row ${index + 1} sites`, row.sites);
    put(`Coat row ${index + 1} wireless`, row.wireless);
    put(`Coat row ${index + 1} non-wireless`, row.nonWireless);
    put(`Coat row ${index + 1} shares action cells`, row.continueFromPrevious ? "yes" : "no");
  });
  put("Closing", body.closing);
  return map;
}

export function diffCoatCallout(previous: CoatCalloutBody, next: CoatCalloutBody): CoatFieldChange[] {
  const before = fieldMap(previous);
  const after = fieldMap(next);
  const names = new Set<string>([...before.keys(), ...after.keys()]);
  const changes: CoatFieldChange[] = [];
  for (const fieldName of names) {
    const changedFrom = before.get(fieldName) ?? "";
    const changedTo = after.get(fieldName) ?? "";
    if (changedFrom !== changedTo) changes.push({ fieldName, changedFrom, changedTo });
  }
  changes.sort((left, right) => left.fieldName.localeCompare(right.fieldName));
  return changes;
}

/** First controlled document revision is 01. Letters from older rows count as 01, 02, and so on. */
export function nextRevisionAfter(revision: string): string {
  const trimmed = revision.trim();
  let value = 0;
  if (/^\d+$/.test(trimmed)) {
    value = Number(trimmed);
  } else if (/^[A-Za-z]+$/.test(trimmed)) {
    const letters = trimmed.toUpperCase();
    for (let index = 0; index < letters.length; index += 1) {
      value = value * 26 + (letters.charCodeAt(index) - 64);
    }
  }
  return String(Math.max(1, value) + (value >= 1 ? 1 : 0)).padStart(2, "0");
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatLines(value: string): string {
  return value
    .split("\n")
    .map((line) => {
      const top = line.startsWith("Top:");
      const bottom = line.startsWith("Bottom:");
      if (top || bottom) {
        const label = top ? "Top:" : "Bottom:";
        return `<strong>${label}</strong>${escapeHtml(line.slice(label.length))}`;
      }
      return escapeHtml(line);
    })
    .join("<br>");
}

function actionSpan(rows: CoatCalloutRow[], index: number): number {
  let span = 1;
  while (rows[index + span]?.continueFromPrevious) span += 1;
  return span;
}

const PAGE_SCRIPT = `
(function () {
  var pagesRoot = document.getElementById("pages");
  var source = document.getElementById("source");
  if (!pagesRoot || !source) return;
  var nodes = Array.prototype.slice.call(source.children);
  var inner = null;
  function fits(box) { return box.scrollHeight <= box.clientHeight + 1; }
  function newPage() {
    var page = document.createElement("section");
    page.className = "page";
    var body = document.createElement("div");
    body.className = "page-body";
    var foot = document.createElement("div");
    foot.className = "page-foot";
    var template = document.getElementById("footer-template");
    foot.innerHTML = template ? template.innerHTML : "";
    page.appendChild(body);
    page.appendChild(foot);
    pagesRoot.appendChild(page);
    inner = body;
    return body;
  }
  function rowSpanOf(row) {
    var span = 1;
    for (var c = 0; c < row.cells.length; c++) span = Math.max(span, row.cells[c].rowSpan || 1);
    return span;
  }
  function makeTableShell(table) {
    var copy = document.createElement("table");
    copy.className = table.className;
    var colgroup = table.querySelector("colgroup");
    var thead = table.querySelector("thead");
    if (colgroup) copy.appendChild(colgroup.cloneNode(true));
    if (thead) copy.appendChild(thead.cloneNode(true));
    var tbody = document.createElement("tbody");
    copy.appendChild(tbody);
    return { table: copy, tbody: tbody };
  }
  function takeTrailingIntro() {
    var kids = Array.prototype.slice.call(inner.childNodes);
    var moved = [];
    while (kids.length) {
      var last = kids[kids.length - 1];
      if (last.tagName !== "P" && last.tagName !== "H2") break;
      moved.unshift(kids.pop());
    }
    if (!moved.some(function (node) { return node.tagName === "H2"; })) return [];
    return moved;
  }
  function packTable(table) {
    var probe = table.cloneNode(true);
    inner.appendChild(probe);
    if (fits(inner)) return;
    inner.removeChild(probe);
    var intro = takeTrailingIntro();
    if (intro.length && inner.childNodes.length > intro.length) {
      intro.forEach(function (node) { inner.removeChild(node); });
      newPage();
      intro.forEach(function (node) { inner.appendChild(node); });
      probe = table.cloneNode(true);
      inner.appendChild(probe);
      if (fits(inner)) return;
      inner.removeChild(probe);
    }
    var rows = Array.prototype.slice.call(table.querySelectorAll("tbody > tr"));
    var index = 0;
    var open = null;
    function startOnFreshPage() {
      if (inner.childNodes.length) newPage();
      open = makeTableShell(table);
      inner.appendChild(open.table);
    }
    while (index < rows.length) {
      var span = rowSpanOf(rows[index]);
      var group = [];
      for (var g = 0; g < span && index + g < rows.length; g++) group.push(rows[index + g].cloneNode(true));
      if (!open) startOnFreshPage();
      for (var a = 0; a < group.length; a++) open.tbody.appendChild(group[a]);
      if (!fits(inner)) {
        for (var r = 0; r < group.length; r++) open.tbody.removeChild(group[r]);
        var hadRows = open.tbody.rows.length > 0;
        if (!hadRows) inner.removeChild(open.table);
        open = null;
        if (!hadRows && inner.childNodes.length === 0) {
          open = makeTableShell(table);
          inner.appendChild(open.table);
          for (var f = 0; f < group.length; f++) open.tbody.appendChild(group[f]);
          index += group.length;
          open = null;
          newPage();
          continue;
        }
        newPage();
        continue;
      }
      index += group.length;
    }
  }
  newPage();
  var i = 0;
  while (i < nodes.length) {
    var node = nodes[i];
    var next = nodes[i + 1];
    if (node.tagName === "H2" && next) {
      if (inner.childNodes.length) {
        inner.appendChild(node);
        var headingFits = fits(inner);
        inner.removeChild(node);
        if (!headingFits) newPage();
      }
      inner.appendChild(node);
      if (next.tagName === "TABLE") {
        packTable(next);
        i += 2;
        continue;
      }
      inner.appendChild(next);
      if (!fits(inner)) {
        inner.removeChild(next);
        if (inner.childNodes.length > 1) {
          inner.removeChild(node);
          newPage();
          inner.appendChild(node);
        }
        inner.appendChild(next);
        if (!fits(inner) && inner.childNodes.length > 1) {
          inner.removeChild(next);
          newPage();
          inner.appendChild(next);
        }
      }
      i += 2;
      continue;
    }
    if (node.tagName === "TABLE") {
      packTable(node);
      i += 1;
      continue;
    }
    inner.appendChild(node);
    if (!fits(inner)) {
      inner.removeChild(node);
      if (inner.childNodes.length) newPage();
      inner.appendChild(node);
    }
    i += 1;
  }
  var pages = pagesRoot.querySelectorAll(".page");
  for (var p = 0; p < pages.length; p++) {
    var slot = pages[p].querySelector(".pagenum");
    if (slot) slot.innerHTML = '<span class="k">Page</span>' + (p + 1) + " of " + pages.length;
  }
})();
`;

export const WORK_INSTRUCTION_TEMPLATE = "work_instruction";

export type WorkInstructionBody = {
  title: string;
  subtitle: string;
  date: string;
  company: string;
  drawing: string;
  assemblies: string;
  contentHtml: string;
};

const CONTENT_LIMIT = 200_000;

export function sanitizeWorkInstructionHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");
}

export function blankWorkInstruction(dateLabel: string): WorkInstructionBody {
  return {
    title: "New work instruction",
    subtitle: "",
    date: dateLabel,
    company: "Smart Grid Solutions",
    drawing: "",
    assemblies: "",
    contentHtml: "<h2>1. </h2><p></p>",
  };
}

export function conformalCoatWorkInstruction(): WorkInstructionBody {
  const seed = conformalCoatCalloutSeed();
  return {
    title: seed.title,
    subtitle: `${seed.wirelessAssembly} and ${seed.nonWirelessAssembly}`,
    date: seed.date,
    company: seed.company,
    drawing: seed.drawing,
    assemblies: `${seed.wirelessAssembly} and ${seed.nonWirelessAssembly}`,
    contentHtml: coatContentHtml(seed),
  };
}

export function parseWorkInstructionBody(value: unknown): WorkInstructionBody {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Work instruction body must be an object.");
  }
  const record = value as Record<string, unknown>;
  if (typeof record.contentHtml !== "string") throw new Error("Body must be text.");
  const contentHtml = sanitizeWorkInstructionHtml(record.contentHtml);
  if (!contentHtml.trim()) throw new Error("Body is required.");
  if (contentHtml.length > CONTENT_LIMIT) throw new Error("Body is too long.");
  return {
    title: requireText(record.title, "Title").trim(),
    subtitle: optionalText(record.subtitle, "Subtitle").trim(),
    date: requireText(record.date, "Date").trim(),
    company: requireText(record.company, "Company").trim(),
    drawing: optionalText(record.drawing, "Drawing").trim(),
    assemblies: optionalText(record.assemblies, "Assemblies").trim(),
    contentHtml,
  };
}

export function diffWorkInstruction(previous: WorkInstructionBody, next: WorkInstructionBody): CoatFieldChange[] {
  const fields: Array<[string, string, string]> = [
    ["Title", previous.title, next.title],
    ["Subtitle", previous.subtitle, next.subtitle],
    ["Date", previous.date, next.date],
    ["Company", previous.company, next.company],
    ["Drawing", previous.drawing, next.drawing],
    ["Assemblies", previous.assemblies, next.assemblies],
    ["Body", previous.contentHtml, next.contentHtml],
  ];
  return fields
    .filter(([, from, to]) => from !== to)
    .map(([fieldName, changedFrom, changedTo]) => ({ fieldName, changedFrom, changedTo }));
}

function coatContentHtml(body: CoatCalloutBody): string {
  const sideRows = body.sides
    .map(
      (side) =>
        `<tr><td>${escapeHtml(side.name)}</td><td>${escapeHtml(side.recognize)}</td><td>${escapeHtml(side.installed)}</td></tr>`,
    )
    .join("");
  const identifyRows = body.identifyRows
    .map(
      (row) =>
        `<tr><td>${escapeHtml(row.assembly)}</td><td>${escapeHtml(row.module)}</td><td>${escapeHtml(row.sim)}</td></tr>`,
    )
    .join("");
  const bullets = body.hybridBullets.map((bullet) => `<li>${escapeHtml(bullet)}</li>`).join("");
  const coatRows = body.coatRows
    .map((row, index) => {
      const previousContinues = index > 0 && row.continueFromPrevious;
      const span = previousContinues ? 0 : actionSpan(body.coatRows, index);
      const actions = previousContinues
        ? ""
        : `<td rowspan="${span}">${formatLines(row.wireless)}</td><td rowspan="${span}">${formatLines(row.nonWireless)}</td>`;
      return `<tr><td class="swatch ${row.color}">${escapeHtml(row.option)}</td><td>${formatLines(row.sites)}</td>${actions}</tr>`;
    })
    .join("");
  return `<p>${escapeHtml(body.intro)}</p>
  <h2>1. Which side is which</h2>
  <table>
    <thead><tr><th>Name</th><th>How to recognize it</th><th>In the device</th></tr></thead>
    <tbody>${sideRows}</tbody>
  </table>
  <p>${escapeHtml(body.sideNote)}</p>
  <h2>2. How to identify the board</h2>
  <p>${escapeHtml(body.identifyNote)}</p>
  <table>
    <thead><tr><th>Assembly</th><th>U3, cellular module, top</th><th>J6, SIM socket, bottom</th></tr></thead>
    <tbody>${identifyRows}</tbody>
  </table>
  <h2>3. Hybrid capacitor</h2>
  <p>${escapeHtml(body.hybridIntro)}</p>
  <ul>${bullets}</ul>
  <div class="note"><p>${escapeHtml(body.cureNote)}</p></div>
  <h2>4. Production coat</h2>
  <p>${escapeHtml(body.coatIntro)}</p>
  <table class="coat">
    <colgroup><col class="option"><col class="sites"><col class="action"><col class="action"></colgroup>
    <thead><tr><th>Drawing option</th><th>Sites</th><th>${escapeHtml(body.wirelessAssembly)} wireless</th><th>${escapeHtml(body.nonWirelessAssembly)} non-wireless</th></tr></thead>
    <tbody>${coatRows}</tbody>
  </table>
  <h2>5. What this replaces</h2>
  <p>${escapeHtml(body.closing)}</p>`;
}

export function renderWorkInstructionHtml(body: WorkInstructionBody, revision: string): string {
  const footer =
    `<div><div><span class="k">Document</span>Work instruction</div>` +
    `<div><span class="k">Title</span>${escapeHtml(body.title)}</div>` +
    `<div><span class="k">Drawing</span>${escapeHtml(body.drawing)}</div>` +
    `<div><span class="k">Assemblies</span>${escapeHtml(body.assemblies)}</div></div>` +
    `<div class="rev"><div><span class="k">Revision</span>${escapeHtml(revision)}</div>` +
    `<div><span class="k">Date</span>${escapeHtml(body.date)}</div>` +
    `<div class="pagenum"></div></div>`;
  const content = sanitizeWorkInstructionHtml(body.contentHtml);
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Work instruction: ${escapeHtml(body.title)}</title>
  <style>
    :root { --ink:#1a1a1a; --rule:#222; --yellow:#f5e27a; --blue:#c5d8f2; --brown:#d7b899; --red:#f0b4b4; --purple:#d9c6ee; --orange:#f6c99a; --grey:#d9d9d9; }
    * { box-sizing: border-box; }
    body { margin:0; color:var(--ink); font:15px/1.45 "Segoe UI", Calibri, sans-serif; background:#d4d4d4; }
    #source { display:none; }
    .page { width:8.5in; height:11in; margin:28px auto; background:#fff; box-shadow:0 1px 6px rgba(0,0,0,.22); position:relative; }
    .page-body { position:absolute; top:.6in; right:.6in; bottom:.95in; left:.6in; overflow:hidden; }
    .page-foot { position:absolute; left:.6in; right:.6in; bottom:.22in; border-top:1px solid #222; padding-top:5px; display:grid; grid-template-columns:1fr auto; column-gap:24px; align-items:start; font-size:11px; line-height:1.35; }
    .page-foot .k { display:inline-block; min-width:5.4em; font-weight:650; }
    .pagenum { white-space:nowrap; }
    header { display:grid; grid-template-columns:1fr auto; gap:12px 24px; border-bottom:3px solid var(--rule); padding-bottom:12px; margin-bottom:18px; }
    h1 { margin:0; font-size:22px; line-height:1.2; }
    .doc-id { margin:4px 0 0; font-size:14px; }
    .status { justify-self:end; text-align:right; font-size:13px; }
    h2 { margin:22px 0 8px; font-size:16px; border-bottom:1px solid #bbb; padding-bottom:3px; }
    p { margin:8px 0; }
    table { width:100%; border-collapse:collapse; margin:8px 0 4px; font-size:13px; }
    th, td { border:1px solid #333; padding:5px 6px; vertical-align:top; text-align:left; }
    .coat col.option { width:16%; } .coat col.sites { width:16%; } .coat col.action { width:34%; }
    th { background:#f3f3f3; }
    .swatch { font-weight:650; }
    .yellow { background:var(--yellow); } .blue { background:var(--blue); } .brown { background:var(--brown); }
    .red { background:var(--red); } .purple { background:var(--purple); } .orange { background:var(--orange); } .grey { background:var(--grey); }
    ul { margin:8px 0; padding-left:1.2em; } li { margin:4px 0; }
    .note { border:1px solid #333; padding:8px 10px; margin:10px 0; } .note p { margin:0; }
    @page { size:letter; margin:0; }
    @media print {
      body { background:#fff; }
      .page { margin:0; box-shadow:none; break-after:page; page-break-after:always; }
      .page:last-child { break-after:auto; page-break-after:auto; }
    }
  </style>
</head>
<body>
<div id="pages"></div>
<template id="footer-template">${footer}</template>
<article id="source">
  <header>
    <div>
      <h1>${escapeHtml(body.title)}</h1>
      <p class="doc-id">${escapeHtml(body.subtitle)}</p>
    </div>
    <div class="status">${escapeHtml(body.date)}<br>${escapeHtml(body.company)}</div>
  </header>
  ${content}
</article>
<script>${PAGE_SCRIPT}</script>
</body>
</html>`;
}

export function renderConformalCoatCalloutHtml(body: CoatCalloutBody, revision: string): string {
  return renderWorkInstructionHtml(
    {
      title: body.title,
      subtitle: `${body.wirelessAssembly} and ${body.nonWirelessAssembly}`,
      date: body.date,
      company: body.company,
      drawing: body.drawing,
      assemblies: `${body.wirelessAssembly} and ${body.nonWirelessAssembly}`,
      contentHtml: coatContentHtml(body),
    },
    revision,
  );
}
